import { useMemo } from 'react'
import { PickList, TextField } from '@bloo-team/ui'
import { readVisualSpec, VISUAL_CHARTS, writeVisualSpec } from '@bloo-team/api-client'
import type { SearchRow, VisualSpec } from '@bloo-team/api-client'
import { AggBar, AggDonut, TimelineChart, toAggData } from '@bloo-team/core-shell'
import { useBlockResult } from '../../../lib/workbook-run'
import { InlineKv, Section } from './parts'
import type { BlockEditorProps, BlockViewProps } from './types'

/**
 * Visualization: charts the PREVIOUS block's result (its task id, read
 * unpaginated) as soon as that block has one — the block has no Run button. Only chart types with a house chart render; the rest show
 * their configuration. The exact type → chart mapping is still open (plan
 * §7, T6e) — this is the provisional one, in one place:
 */
export type HouseChart = 'bar' | 'donut' | 'timeline-bars' | 'timeline-area' | null

export function houseChartFor(chartType: string): HouseChart {
  switch (chartType) {
    case 'bar':
    case 'column':
      return 'bar'
    case 'pie':
    case 'radial':
      return 'donut'
    case 'timeline':
    case 'timebar':
      return 'timeline-bars'
    case 'line':
    case 'area':
      return 'timeline-area'
    default:
      return null
  }
}

/** Time buckets from a result: key[0] is the time field (epoch ms), value the count. */
export function timeBucketsOf(rows: SearchRow[], timeField: string, valueField: string) {
  const buckets = rows
    .map((r) => ({ t: Number(r[timeField]), count: Number(r[valueField]) }))
    .filter((b) => Number.isFinite(b.t) && Number.isFinite(b.count))
    .sort((a, b) => a.t - b.t)
  let slice = Infinity
  for (let i = 1; i < buckets.length; i++) slice = Math.min(slice, buckets[i]!.t - buckets[i - 1]!.t)
  return { buckets, sliceMs: Number.isFinite(slice) && slice > 0 ? slice : 60_000 }
}

/** True when no row carries a value for `field` (absent from the result, or always empty). */
export function hasNoValues(rows: SearchRow[], field: string): boolean {
  return !rows.some((r) => r[field] !== null && r[field] !== undefined && r[field] !== '')
}

function Chart({ spec, rows }: { spec: VisualSpec; rows: SearchRow[] }) {
  const kind = houseChartFor(spec.chartType)
  const keyField = spec.keys[0] ?? ''
  const agg = useMemo(() => (keyField && spec.value ? toAggData(rows, keyField, spec.value) : []), [rows, keyField, spec.value])
  const time = useMemo(() => timeBucketsOf(rows, keyField, spec.value), [rows, keyField, spec.value])
  if (!kind) return <p className="hud-unit">{spec.chartType} charts are not rendered yet — configuration below.</p>
  if (!keyField || !spec.value) return <p className="hud-unit">Choose a key field and a value field to draw the chart.</p>
  // A field the source never fills would chart as one "–" bar: say so instead.
  for (const [field, role] of [[keyField, 'key'], [spec.value, 'value']] as const) {
    if (rows.length && hasNoValues(rows, field)) {
      return (
        <p className="hud-unit" role="status">
          {field} has no values in the previous block’s result — recheck the {role} field.
        </p>
      )
    }
  }
  if (kind === 'bar') return agg.length ? <AggBar data={agg} field={keyField} /> : <p className="hud-unit">Nothing to chart.</p>
  if (kind === 'donut') return agg.length ? <AggDonut data={agg} field={keyField} /> : <p className="hud-unit">Nothing to chart.</p>
  return time.buckets.length ? (
    <TimelineChart
      buckets={time.buckets}
      sliceMs={time.sliceMs}
      style={kind === 'timeline-area' ? 'area' : 'bars'}
      onSelectRange={() => {}}
    />
  ) : (
    <p className="hud-unit">No time buckets in {keyField}.</p>
  )
}

export function VisualView({ block, clusterId, scopeId, sourceTaskId }: BlockViewProps & { sourceTaskId: string | null }) {
  const spec = readVisualSpec(block)
  const result = useBlockResult({ clusterId, scopeId, taskId: sourceTaskId, all: true })
  return (
    <div className="wb-secs">
      <div className="wb-sec">
        <div className="wb-lead">
          <span className="wb-lead-name">{spec.widgetName || 'Untitled widget'}</span>
          <span className="bui-chip wb-chip-mono">{spec.chartType || '—'} · {spec.variant || 'default'}</span>
        </div>
      </div>
      {/* The chart appears once the block above has a result; nothing is drawn before. */}
      {sourceTaskId === null ? null : (
        <div className="wb-sec">
          {result.isPending ? (
            <p className="hud-unit" role="status">Loading the chart data…</p>
          ) : result.isError || !result.data || result.data.failed ? (
            <p className="hud-unit">The previous block’s result could not be read.</p>
          ) : (
            <Chart spec={spec} rows={result.data.rows} />
          )}
          {spec.variant && spec.variant !== 'default' && houseChartFor(spec.chartType) ? (
            <span className="bui-hint">{spec.variant} shows as a single series for now.</span>
          ) : null}
        </div>
      )}
      <Section label="Data">
        <InlineKv items={[['keys', spec.keys.filter(Boolean).join(', ')], ['value', spec.value], ['dataframe', spec.dataframe ?? '']]} />
      </Section>
    </div>
  )
}

export function VisualEditor({ block, index, onChange, prevFields }: BlockEditorProps) {
  const spec = readVisualSpec(block)
  const set = (patch: Partial<VisualSpec>) => onChange(writeVisualSpec(block, { ...spec, ...patch }))
  const charts = Object.keys(VISUAL_CHARTS)
  const variants = VISUAL_CHARTS[spec.chartType] ?? [spec.variant || 'default']
  const fieldOptions = [...new Set([...prevFields, ...spec.keys.filter((k): k is string => !!k), spec.value].filter(Boolean))]
  const setKey = (slot: number, v: string | null) => {
    const keys = [...spec.keys]
    while (keys.length <= slot) keys.push(null)
    keys[slot] = v
    set({ keys })
  }
  return (
    <div className="wb-secs">
      <Section label="Widget">
        <div className="wb-fields">
          <TextField className="wb-w-lg" label="widget name" value={spec.widgetName} onChange={(v) => set({ widgetName: v })} />
        </div>
      </Section>
      <Section label="Chart">
        <div className="wb-fields">
          <PickList
            label="Chart type"
            options={charts.includes(spec.chartType) || !spec.chartType ? charts : [spec.chartType, ...charts]}
            selected={spec.chartType ? [spec.chartType] : []}
            onChange={(next) => {
              const chartType = next[0] ?? spec.chartType
              set({ chartType, variant: (VISUAL_CHARTS[chartType] ?? ['default'])[0] ?? 'default' })
            }}
          />
          <PickList
            label="Variant"
            options={variants}
            selected={[spec.variant || 'default']}
            onChange={(next) => set({ variant: next[0] ?? spec.variant })}
          />
        </div>
      </Section>
      <Section label="Data">
        <div className="wb-fields">
          {[0, 1, 2].map((slot) => (
            <PickList
              key={slot}
              label={`Block ${index + 1} key ${slot + 1}`}
              placeholder={slot === 0 ? 'key…' : 'optional…'}
              options={fieldOptions}
              selected={spec.keys[slot] ? [spec.keys[slot] as string] : []}
              onChange={(next) => setKey(slot, next[0] ?? null)}
            />
          ))}
          <PickList
            label="Value"
            placeholder="value…"
            options={fieldOptions}
            selected={spec.value ? [spec.value] : []}
            onChange={(next) => set({ value: next[0] ?? '' })}
          />
        </div>
      </Section>
    </div>
  )
}
